import type { Patient } from '@medplum/fhirtypes'
import { invariant } from '@seen/core/invariant'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import {
  Participant,
  type ParticipantReferenceString,
} from '@seen/core/models/patients/participant'
import { isOfType, parseReferenceString } from '@seen/core/utils'
import { KBD, TypographyDetail } from '@seen/ui'
import { IconArrowUp } from '@tabler/icons-react'
import { compact } from 'lodash-es'
import { type JSX, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogFooter } from '#src/components/core/dialog/DialogFooter.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { SubmitButton } from '#src/components/core/form/SubmitButton.tsx'
import { TextLink } from '#src/components/core/TextLink.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { ParticipantComboboxButton } from '#src/components/ParticipantComboboxButton.tsx'
import { extractMentionReferences } from '#src/components/rich-text-editor/mention/extractMentionReferences.ts'
import { RequiredAnswersDialog } from '#src/components/rich-text-editor/required/RequiredAnswersDialog.tsx'
import { useHeldAnswers } from '#src/components/rich-text-editor/required/useHeldAnswers.ts'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { Hotkeys } from '#src/constants/hotkeys.ts'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useForm } from '#src/hooks/useForm.ts'
import { useHotkeys } from '#src/hooks/useHotkeys.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useTaskComposerStore } from '#src/hooks/useTaskComposerStore.ts'
import { useTimelineItemUrl } from '#src/hooks/useTimelineItemUrl.ts'
import { useTimelineNoteComposerDialogStore } from '#src/hooks/useTimelineNoteComposerDialogStore.ts'
import { extractTitleFromHTML } from '#src/utils/html.ts'
import { trimHTML } from '#src/utils/trimHTML.ts'

export function TimelineNoteComposerDialog(): JSX.Element | null {
  const isReadOnly = useIsReadOnly()
  const open = useTimelineNoteComposerDialogStore((state) => state.open)
  const setOpen = useTimelineNoteComposerDialogStore((state) => state.setOpen)

  useHotkeys(
    Hotkeys.TimelineNoteComposerDialog,
    () =>
      setOpen(true, {
        onSuccess: (comment) => {
          toast.success(<TimelineNoteCreatedToast comment={comment} />)
        },
      }),
    {
      preventDefault: true,
      enabled: !isReadOnly,
    }
  )

  if (isReadOnly) {
    return null
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogDisplay>
        <TimelineNoteComposerDialogContent />
      </DialogDisplay>
    </Dialog>
  )
}

function TimelineNoteComposerDialogContent(): JSX.Element {
  const storeMessage = useTimelineNoteComposerDialogStore(
    (state) => state.message
  )
  const setStoreMessage = useTimelineNoteComposerDialogStore(
    (state) => state.setMessage
  )

  const defaultTaskComposerParticipant = useTaskComposerStore(
    (state) => state.currentParticipant
  )

  const form = useForm({
    schema: formSchema,
    defaultValues: {
      message: storeMessage,
      participant: defaultTaskComposerParticipant?.reference ?? null,
    },
  })

  const setOpen = useTimelineNoteComposerDialogStore((state) => state.setOpen)

  const storeOnSuccess = useTimelineNoteComposerDialogStore(
    (state) => state.onSuccess
  )

  const currentPractitioner = useCurrentPractitioner()

  const { mutateAsync: createComment, isPending: isCreatingComment } =
    useCreateMutation<CommentCommunication>({
      disableDefaultErrorToast: true,
      mutation: {
        onSuccess: () => {
          setStoreMessage('')
        },
        onError: (error, comment) => {
          console.error(error, comment)
          toast.error(
            error instanceof Error
              ? `Failed to create note "${extractTitleFromHTML(comment.message)}": ${error.message}`
              : `Failed to create note "${extractTitleFromHTML(comment.message)}"`
          )
        },
      },
    })

  const participantReferenceString = form.watch('participant')

  const participantReference = useMemo(() => {
    return parseReferenceString<Patient>(participantReferenceString)
  }, [participantReferenceString])

  const { data: participant } = useRead(Participant, participantReference)

  const related = useMemo(() => {
    return participantReference ? [participantReference] : undefined
  }, [participantReference])

  // Answers given in the note are recorded once it is added.
  const held = useHeldAnswers({ surface: 'timeline-note' })

  const submit = form.handleSubmit(async (values) => {
    if (!values.participant) {
      return
    }

    const participantReference = parseReferenceString<Patient>(
      values.participant
    )
    invariant(participantReference, 'Invalid participant reference')
    // Taken now, since the composer may close before the note exists.
    const prepared = await held.prepare(values.message)
    if (!prepared) {
      return
    }

    /* Chained on the promise rather than a per-call `onSuccess`, which is
     * dropped once the dialog has closed: the answers still have to be
     * recorded. A failure is reported by the mutation's own `onError`. */
    createComment(
      new CommentCommunication({
        message: prepared.message,
        sender: currentPractitioner.reference,
        partOf: [participantReference],
      })
    ).then(
      (createdComment) => {
        held.record(prepared.answers, {
          noteKey: createdComment.reference.reference ?? '',
          subject: participantReference,
        })
        if (storeOnSuccess) {
          storeOnSuccess(createdComment)
          setOpen(false)
        }
        form.reset()
      },
      () => {}
    )

    if (!storeOnSuccess) {
      setOpen(false)
    }
  })

  const [participantReferences, setParticipantReferences] = useState<
    ParticipantReferenceString[]
  >([])

  return (
    <Form onSubmit={submit}>
      <DialogContent className="pt-6">
        <FormField
          control={form.control}
          name="message"
          render={({ field: { onChange, ...rest } }) => {
            return (
              <RichTextEditor
                editorRef={held.editorRef}
                observationRecordStatus="deferred"
                placeholder="New chart note…"
                variant="unstyled"
                className="flex-1"
                contentClassName="text-sm"
                enablePractitionerMention
                related={related}
                participant={participant}
                onChange={(value) => {
                  onChange(value)
                  setStoreMessage(value)
                  const mentionReferences = extractMentionReferences(value)
                  const extractedParticipantReferences = compact(
                    mentionReferences
                      .filter((reference) => isOfType(reference, 'Patient'))
                      .map((r) => r.reference)
                  )
                  const newParticipantReferences =
                    extractedParticipantReferences.filter(
                      (r) => !participantReferences.includes(r)
                    )
                  setParticipantReferences(extractedParticipantReferences)
                  if (newParticipantReferences.length > 0) {
                    form.setValue('participant', newParticipantReferences[0])
                  }
                }}
                {...rest}
              />
            )
          }}
        />
      </DialogContent>
      <DialogFooter>
        <FormField
          control={form.control}
          name="participant"
          render={({ field }) => <ParticipantComboboxButton {...field} />}
        />
        <Tooltip>
          <TooltipTrigger asChild>
            <SubmitButton
              size="icon"
              className="self-end rounded-full"
              submitting={isCreatingComment}
            >
              <IconArrowUp className="size-4" />
            </SubmitButton>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            className="flex items-center gap-1 px-2"
          >
            <div className="flex gap-1">
              <KBD>⌘</KBD>
              <KBD>Enter</KBD>
            </div>
            <TypographyDetail className="text-foreground-secondary">
              to submit
            </TypographyDetail>
          </TooltipContent>
        </Tooltip>
      </DialogFooter>
      <RequiredAnswersDialog {...held.dialog} />
    </Form>
  )
}

const formSchema = z.object({
  message: z
    .string()
    .transform(trimHTML)
    .refine((v) => v.length > 0, { message: 'Note required' }),
  participant: z.string().nullable(),
})

export function TimelineNoteCreatedToast({
  comment,
}: {
  comment: CommentCommunication
}): JSX.Element {
  const url = useTimelineItemUrl(comment)
  const title = extractTitleFromHTML(comment.message)
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      <TypographyDetail>Note created</TypographyDetail>
      <div className="flex">
        <p className="text-sm font-medium">{title}</p>
      </div>
      <div className="flex">
        <TextLink to={url}>View note</TextLink>
      </div>
    </div>
  )
}
